/**
 * 画像プロンプトデータ管理クラス
 */

import type { BaseManager, SavedImagePrompt } from './types'

export class ImagePromptsManager implements BaseManager {
  constructor(public db: IDBDatabase | null) {}

  /**
   * 画像プロンプトを保存
   */
  async saveImagePrompt(prompt: SavedImagePrompt): Promise<void> {
    if (!this.db) throw new Error('Database not initialized')
    
    const transaction = this.db.transaction(['imagePrompts'], 'readwrite')
    const store = transaction.objectStore('imagePrompts')
    
    return new Promise((resolve, reject) => {
      const request = store.put(prompt)
      
      request.onsuccess = () => resolve()
      request.onerror = () => reject(new Error('Failed to save image prompt'))
    })
  }

  /**
   * 画像プロンプトを取得
   */
  async getImagePrompt(id: string): Promise<SavedImagePrompt | undefined> {
    if (!this.db) throw new Error('Database not initialized')
    
    const transaction = this.db.transaction(['imagePrompts'], 'readonly')
    const store = transaction.objectStore('imagePrompts')
    
    return new Promise((resolve, reject) => {
      const request = store.get(id)
      
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(new Error('Failed to get image prompt'))
    })
  }

  /**
   * すべての画像プロンプトを取得
   */
  async getAllImagePrompts(): Promise<SavedImagePrompt[]> {
    if (!this.db) throw new Error('Database not initialized')
    
    const transaction = this.db.transaction(['imagePrompts'], 'readonly')
    const store = transaction.objectStore('imagePrompts')
    
    return new Promise((resolve, reject) => {
      const request = store.getAll()
      
      request.onsuccess = () => {
        const prompts = request.result
        // createdAtで降順ソート
        prompts.sort((a, b) => 
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )
        resolve(prompts)
      }
      request.onerror = () => reject(new Error('Failed to get all image prompts'))
    })
  }

  /**
   * 画像プロンプトを更新
   */
  async updateImagePrompt(id: string, updates: Partial<SavedImagePrompt>): Promise<void> {
    if (!this.db) throw new Error('Database not initialized')
    
    const existing = await this.getImagePrompt(id)
    if (!existing) throw new Error('Image prompt not found')
    
    const updated = {
      ...existing,
      ...updates,
      updatedAt: new Date().toISOString()
    }
    
    const transaction = this.db.transaction(['imagePrompts'], 'readwrite')
    const store = transaction.objectStore('imagePrompts')
    
    return new Promise((resolve, reject) => {
      const request = store.put(updated)
      
      request.onsuccess = () => resolve()
      request.onerror = () => reject(new Error('Failed to update image prompt'))
    })
  }

  /**
   * 画像プロンプトを削除
   */
  async deleteImagePrompt(id: string): Promise<void> {
    if (!this.db) throw new Error('Database not initialized')
    
    const transaction = this.db.transaction(['imagePrompts'], 'readwrite')
    const store = transaction.objectStore('imagePrompts')
    
    return new Promise((resolve, reject) => {
      const request = store.delete(id)
      
      request.onsuccess = () => resolve()
      request.onerror = () => reject(new Error('Failed to delete image prompt'))
    })
  }

  /**
   * すべての画像プロンプトを削除
   */
  async deleteAllImagePrompts(): Promise<void> {
    if (!this.db) throw new Error('Database not initialized')
    
    const transaction = this.db.transaction(['imagePrompts'], 'readwrite')
    const store = transaction.objectStore('imagePrompts')
    
    return new Promise((resolve, reject) => {
      const request = store.clear()
      
      request.onsuccess = () => resolve()
      request.onerror = () => reject(new Error('Failed to delete all image prompts'))
    })
  }
}